export enum DBMS_STATUS {
  STARTED = 'started',
  STOPPED = 'stopped',
}

export enum DBMS_SERVER_STATUS {
  ONLINE = 'online',
  OFFLINE = 'offline',
  UNKNOWN = 'unknown',
}

export enum NEO4J_EDITION {
  COMMUNITY = 'community',
  ENTERPRISE = 'enterprise',
}

export type ElectronAppInfo = {
  platform: string;
  version: string;
  appPath: string;
  appLogPath: string;
  dataPath: string;
  cachePath: string;
  configPath: string;
};

export type SelectedFile = {
  filePath: string;
  fileName: string;
  dirName: string;
};

export type Dbms = {
  id: string;
  name: string;
  description: string;
  tags: string[];
  metadata: Record<string, unknown>;
  connectionUri: string;
};

export type DbmsInfo = Dbms & {
  status: DBMS_STATUS;
  serverStatus: DBMS_SERVER_STATUS;
  edition?: NEO4J_EDITION;
  version?: string;
  prerelease?: string;
  rootPath?: string;
};

export type DbmsVersion = {
  edition: NEO4J_EDITION;
  version: 'string';
  origin: 'cached' | 'online';
  dist: 'string';
};

export type DbmsPlugin = {
  name: string;
  version: string;
  filePath: string;
  pendingRestart: boolean;
};

export type DbmsInstallArgs = {
  name: string;
  credentials: string;
  version: string;
  edition?: NEO4J_EDITION;
  environmentNameOrId?: string;
  noCaching?: boolean;
  limited?: boolean;
  installPath?: string;
};

export type DbmsPluginInstallArgs = {
  dbmsId: string;
  pluginName: string;
};

export type RemoteConnection = {
  id: string;
  name: string;
  connectionUri: string;
};

export type OperationName =
  | 'dbmsInstall'
  | 'dbmsUpgrade'
  | 'dbmsUninstall'
  | 'dbmsPluginInstall'
  | 'javaDownload'
  | 'test';

export enum PROGRESS_STATUS {
  LOADING = 'LOADING',
  SUCCESS = 'SUCCESS',
  ERROR = 'ERROR',
}

export type ProgressEvent = {
  operation: OperationName;

  /**
   * ID used to identify specific instances of an operation. For example if the
   * operation is dbmsInstall, the ID would refer to a specific dbmsInstall
   * operation.
   */
  operationId: string;

  /**
   * User facing message describing the sub-task tracked by the event.
   *
   * @example "Downloading Neo4j archive"
   */
  message: string;
  status: PROGRESS_STATUS;
  percent?: number;
  error?: unknown;
};

export type CredentialsType = 'dbms' | 'connection';

export type UserSettings = {
  storePasswords: boolean;
  theme: 'system' | 'light' | 'dark';
  acceptedLicenseAgreement: boolean;
};

export type Db4 = {
  name: string;
  address: string;
  role: string;
  requestedStatus: string;
  currentStatus: string;
  error: string;
  default: boolean;
  home: boolean;
};

export type Db5 = {
  name: string;
  type: string;
  aliases: string;
  access: string;
  address: string;
  role: string;
  writer: string;
  requestedStatus: string;
  currentStatus: string;
  statusMessage: string;
  default: boolean;
  home: boolean;
  constituents: string;
};

export type DbInfo = {
  databaseName: string;
  inUse: boolean;
  storeFormat: string | null;
  storeFormatIntroduced: string | null;
  storeFormatSuperseeded: string | null;
  recoveryRequired: boolean;
};
